/**
 * Resolves the bundle visibility rules shared by both XLSX producers: which
 * member rows leave the sheet, which member cells are blanked, and which rows
 * are bundle headers whose cells the header reader resolves (SYD-2276/2279).
 *
 * Consumed by `useArTableExport` ("Export This Table as XLSX") and by
 * `createArTableExportSnapshot` (email / workflow / form sheets).
 *
 * Callers pass the post-split, post-column-filter row set, already widened by
 * `admitBundleHeaders` — the grid's own classification universe — and intersect
 * `visibleRowIds` with the user's selection afterwards: classifying a selection
 * subset orphans members whose header is unticked. A member whose header is
 * absent from the row set has no resolvable bundle type and fails closed.
 *
 * Both maps come from the one `classifyArBundleRows` run below, so no row can
 * appear in both. `bundleTypeByHeaderRowId` is empty unless kits or groups are
 * enabled, and an empty map means header rows export their own raw cells,
 * matching the grid's flat path; the member-cell rule is deliberately ungated.
 */

import type {
  ArBundleType,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
} from '#types/System.Artifacts.types';
import type { VisibilityType, VisibilityTypeId } from '#types/System.types';
import type { DocEntry } from '@/store/Store.types';
import { classifyArBundleRows } from '@/components/ArTable/classifyArBundleRows';
import { isBundleMemberCellSuppressed } from '@/components/ArTable/components/ArCell/isBundleMemberCellSuppressed';

type ArTableBundleExportVisibility = {
  visibleRowIds: ArRowId[];
  isCellSuppressed: (rowId: ArRowId, columnId: string) => boolean;
  /**
   * Bundle type of every header row in the classified set, for
   * `createBundleHeaderCellReader`. Empty while both bundle flags are off.
   */
  bundleTypeByHeaderRowId: ReadonlyMap<ArRowId, ArBundleType>;
};

function resolveArTableBundleExportVisibility(params: {
  rowIds: readonly ArRowId[];
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>>;
  columns: readonly ArColumnDefinition[];
  visibilityMode: VisibilityTypeId | undefined;
  visibilityType: VisibilityType | undefined;
  kitsEnabled: boolean;
  groupsEnabled: boolean;
}): ArTableBundleExportVisibility {
  const classification = classifyArBundleRows({ rowIds: params.rowIds, rowsDocs: params.rowsDocs });

  const activeVisibilityType =
    params.visibilityMode && params.visibilityMode !== 'internal' ? params.visibilityType : undefined;
  const hideKitMembers = params.kitsEnabled && activeVisibilityType?.hideKitComponents === true;
  const hideGroupMembers = params.groupsEnabled && activeVisibilityType?.hideGroupComponents === true;

  /**
   * A member whose header is absent from this row set has no resolvable bundle
   * type, so neither hide flag can be ruled out and it fails closed — matching
   * `resolveArBundleDisplay`.
   */
  const visibleRowIds = classification.items
    .filter((row) => {
      if (row.kind !== 'bundleMember') return true;
      if (row.bundleType === 'kit') return !hideKitMembers;
      if (row.bundleType === 'group') return !hideGroupMembers;
      return !hideKitMembers && !hideGroupMembers;
    })
    .map((row) => row.rowId);

  /**
   * Ungated by the table's bundle flags, unlike the row rule: `ArCell` blanks a
   * member cell from the column's hide list alone — through `ArTableBody`'s
   * `flatMemberBundleTypes` when bundles are off, and through
   * `resolveArBundleDisplay`, which assigns member identity without consulting
   * either flag, when only one type is on. `isBundleMemberCellSuppressed` is
   * inert on a column carrying no hide list, so registering every member
   * reproduces the grid in all four flag combinations.
   */
  const memberBundleTypes = new Map<ArRowId, ArBundleType | undefined>(
    classification.items.flatMap((row) =>
      row.kind === 'bundleMember' ? [[row.rowId, row.bundleType] as const] : [],
    ),
  );

  /**
   * Gated on the two flags together, unlike the cell rule above: header-cell
   * resolution exists only on `ArTableBody`'s bundle render path, which it takes
   * when either flag is on. With both off, rows keep their persisted
   * `bundleRole` but render flat with every own cell showing, so an empty map
   * here is what makes the sheet agree with that flat path.
   */
  const bundlesEnabled = params.kitsEnabled || params.groupsEnabled;
  const bundleTypeByHeaderRowId = new Map<ArRowId, ArBundleType>(
    bundlesEnabled
      ? classification.items.flatMap((row) =>
        row.kind === 'bundleHeader' ? [[row.rowId, row.bundleType] as const] : [],
      )
      : [],
  );

  const columnsByTypeId = new Map(params.columns.map((col) => [col.typeId, col] as const));

  return {
    visibleRowIds,
    bundleTypeByHeaderRowId,
    /**
     * Membership is the map key, not a truthy bundle type: a member whose header
     * is outside this row set is registered with an unresolved type, and the
     * predicate fails closed on it rather than being skipped here.
     */
    isCellSuppressed: (rowId, columnId) => {
      const column = columnsByTypeId.get(columnId);
      if (!column || !memberBundleTypes.has(rowId)) return false;
      return isBundleMemberCellSuppressed(
        'member',
        memberBundleTypes.get(rowId),
        params.visibilityMode,
        column,
      );
    },
  };
}

export { resolveArTableBundleExportVisibility };
